import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Star, MapPin, Phone, ArrowLeft,
  CheckCircle, XCircle, AlertCircle, MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyBookingsPage({ user }) {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Review dialog
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Cancel dialog
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.error('Please login to view bookings');
      navigate('/auth');
      return;
    }
    fetchBookings();
  }, [user, navigate]);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/pandit/bookings/devotee`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(res.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelBooking) return;
    
    setCancelling(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const headers = { Authorization: `Bearer ${token}` };

      // If paid, process refund first
      if (cancelBooking.payment_status === 'paid') {
        await axios.post(`${API}/payments/refund/${cancelBooking.id}`, {}, { headers });
        toast.success('Booking cancelled. Full refund initiated!');
      } else {
        await axios.put(`${API}/pandit/booking/${cancelBooking.id}/status`, {
          status: 'cancelled'
        }, { headers });
        toast.success('Booking cancelled');
      }
      
      setCancelBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewBooking) return;
    
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/pandit/review`, {
        booking_id: reviewBooking.id,
        rating: reviewRating,
        review_text: reviewText || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Review submitted. Thank you!');
      setReviewBooking(null);
      setReviewRating(5);
      setReviewText('');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const filterBookings = (status) => {
    if (status === 'all') return bookings;
    if (status === 'active') return bookings.filter(b => ['pending', 'accepted'].includes(b.status));
    return bookings.filter(b => b.status === status);
  };

  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' },
    accepted: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle, label: 'Accepted' },
    completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Completed' },
    rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
    cancelled: { color: 'bg-gray-100 text-gray-700', icon: AlertCircle, label: 'Cancelled' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-3 animate-pulse">ॐ</div>
          <p className="text-gray-500">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF9933] to-[#FF6600] text-white px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-2"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
            My Pooja Bookings
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{bookings.length}</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">
              {bookings.filter(b => ['pending', 'accepted'].includes(b.status)).length}
            </div>
            <div className="text-xs text-gray-500">Active</div>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <div className="text-2xl font-bold text-green-600">
              {bookings.filter(b => b.status === 'completed').length}
            </div>
            <div className="text-xs text-gray-500">Completed</div>
          </div>
        </div>

        {/* Bookings List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
              {['all', 'active', 'completed'].map((tab) => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF9933] data-[state=active]:text-[#FF9933] px-4 py-3 capitalize"
                >
                  {tab}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="p-4">
              {filterBookings(activeTab).length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 mb-4">No bookings found</p>
                  <Button
                    onClick={() => navigate('/pandit')}
                    className="rounded-full bg-[#FF9933] hover:bg-[#E68A00]"
                  >
                    Find Pandit Ji
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filterBookings(activeTab).map((booking) => {
                    const status = statusConfig[booking.status];
                    const StatusIcon = status.icon;
                    
                    return (
                      <div
                        key={booking.id}
                        className="border rounded-xl p-4 hover:border-orange-200 transition-colors"
                        data-testid={`booking-card-${booking.id}`}
                      >
                        <div className="flex items-start gap-4">
                          {/* Pandit Avatar */}
                          <img
                            src={booking.pandit_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(booking.pandit_name)}&background=FF9933&color=fff&size=60`}
                            alt={booking.pandit_name}
                            className="w-14 h-14 rounded-xl object-cover cursor-pointer"
                            onClick={() => navigate(`/pandit/${booking.pandit_id}`)}
                          />
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 
                                className="font-semibold text-gray-900 cursor-pointer hover:text-[#FF9933]"
                                onClick={() => navigate(`/pandit/${booking.pandit_id}`)}
                              >
                                {booking.pandit_name}
                              </h3>
                              <Badge className={`${status.color} hover:${status.color} text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {status.label}
                              </Badge>
                            </div>
                            
                            <p className="font-medium text-[#FF9933] mb-2">{booking.pooja_type}</p>
                            
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {booking.booking_date}
                              </span>
                              {booking.booking_time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5" />
                                  {booking.booking_time}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {booking.city}
                              </span>
                            </div>
                            
                            <div className="flex items-center justify-between mt-3 pt-3 border-t">
                              <div>
                                <span className="font-semibold text-lg">₹{booking.agreed_fee}</span>
                                {booking.payment_status && (
                                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                    booking.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                                    booking.payment_status === 'refunded' ? 'bg-purple-100 text-purple-700' :
                                    booking.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-500'
                                  }`} data-testid={`payment-status-${booking.id}`}>
                                    {booking.payment_status === 'paid' ? 'Paid' :
                                     booking.payment_status === 'refunded' ? 'Refunded' :
                                     booking.payment_status === 'pending' ? 'Payment Pending' : ''}
                                  </span>
                                )}
                              </div>
                              
                              <div className="flex gap-2">
                                {/* Call Pandit */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full"
                                  asChild
                                >
                                  <a href={`tel:${booking.pandit_phone}`}>
                                    <Phone className="w-4 h-4 mr-1" />
                                    Call
                                  </a>
                                </Button>

                                {/* Chat (for accepted/completed or paid bookings) */}
                                {(['accepted', 'completed'].includes(booking.status) || booking.payment_status === 'paid') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-full text-orange-600 border-orange-200 hover:bg-orange-50"
                                    onClick={() => navigate(`/pandit/chat?booking=${booking.id}`)}
                                    data-testid={`chat-btn-${booking.id}`}
                                  >
                                    <MessageCircle className="w-4 h-4 mr-1" />
                                    Chat
                                  </Button>
                                )}
                                
                                {/* Cancel (only for pending/accepted) */}
                                {['pending', 'accepted'].includes(booking.status) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-full text-red-500 border-red-200 hover:bg-red-50"
                                    onClick={() => setCancelBooking(booking)}
                                  >
                                    Cancel
                                  </Button>
                                )}
                                
                                {/* Review (only for completed without review) */}
                                {booking.status === 'completed' && (
                                  <Button
                                    size="sm"
                                    className="rounded-full bg-[#FF9933] hover:bg-[#E68A00]"
                                    onClick={() => setReviewBooking(booking)}
                                  >
                                    <Star className="w-4 h-4 mr-1" />
                                    Review
                                  </Button>
                                )}
                              </div>
                            </div>
                            
                            {/* Rejection reason */}
                            {booking.status === 'rejected' && booking.rejection_reason && (
                              <div className="mt-3 p-3 bg-red-50 rounded-lg text-sm text-red-600">
                                <strong>Reason:</strong> {booking.rejection_reason}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Tabs>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={!!cancelBooking} onOpenChange={() => setCancelBooking(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-gray-600">
              Are you sure you want to cancel this booking for <strong>{cancelBooking?.pooja_type}</strong>?
            </p>
            {cancelBooking?.payment_status === 'paid' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3" data-testid="refund-notice">
                <p className="text-sm text-green-700 font-medium">Full refund of ₹{cancelBooking?.agreed_fee} will be processed</p>
                <p className="text-xs text-green-600 mt-1">Refund will be credited to your original payment method within 5-7 business days</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelBooking(null)} className="rounded-xl">
              No, Keep It
            </Button>
            <Button 
              onClick={handleCancelBooking} 
              disabled={cancelling}
              className="rounded-xl bg-red-500 hover:bg-red-600"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? 'Processing...' : cancelBooking?.payment_status === 'paid' ? 'Cancel & Refund' : 'Yes, Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={!!reviewBooking} onOpenChange={() => {
        setReviewBooking(null);
        setReviewRating(5);
        setReviewText('');
      }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Review {reviewBooking?.pandit_name}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label>Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= reviewRating 
                          ? 'text-yellow-500 fill-yellow-500' 
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="review">Your Review (Optional)</Label>
              <Textarea
                id="review"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience..."
                rows={4}
                className="rounded-xl mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setReviewBooking(null)} 
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitReview} 
              disabled={submittingReview}
              className="rounded-xl bg-[#FF9933] hover:bg-[#E68A00]"
            >
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
